import {
  AssistantChatAction,
  AssistantChatAnswered,
  AssistantChatClosed,
  AssistantChatFeedback,
  AssistantChatInstructionsFailed,
  AssistantChatInstructionsSaved,
  AssistantChatPrompt,
} from '../../interfaces/assistant-chat.interface';

export interface AssistantChatTracker {
  track(event: string, properties: Record<string, unknown>): void;
}

export interface AssistantChatReader {
  threadId?: string;
  messageCount?: number;
}

export interface AssistantChatUser {
  id?: string;
  role?: string;
}

export function readerContext(chat?: AssistantChatReader, user?: AssistantChatUser): Record<string, unknown> {
  return {
    threadId: chat?.threadId,
    messages: chat?.messageCount ?? 0,
    userId: user?.id,
    userRole: user?.role,
  };
}

export class AssistantChatTracking {
  constructor(
    private readonly tracker: AssistantChatTracker,
    private readonly context: () => Record<string, unknown>,
  ) {}

  public opened(): void {
    this.tracker.track('Open Burt Assistant', this.context());
  }

  public closed(event: AssistantChatClosed): void {
    this.tracker.track('Burt Assistant closed', {...this.context(), ...event});
  }

  public prompted(prompt: AssistantChatPrompt): void {
    this.tracker.track('Burt Assistant prompt', {
      ...this.context(),
      origin: prompt.origin,
      suggestionId: prompt.suggestionId ?? 'free-text',
    });
  }

  public answered(event: AssistantChatAnswered): void {
    this.tracker.track('Burt Assistant answer', {
      ...this.context(),
      turn: event.index,
      requestId: event.requestId,
      latencyMs: event.latencyMs,
      failed: event.failed,
      sections: event.sectionTypes,
      caveats: event.caveats,
      followups: event.followups,
      origin: event.origin,
      suggestionId: event.suggestionId,
    });
  }

  public rated(event: AssistantChatFeedback): void {
    this.tracker.track('Burt Assistant feedback', {
      ...this.context(),
      turn: event.index,
      rating: event.rating,
      requestId: event.requestId,
    });
  }

  public acted(action: AssistantChatAction): void {
    const links = action.studio ?? (action.report ? [action.report] : []);
    this.tracker.track('Burt Assistant action', {
      ...this.context(),
      action: action.id,
      ...(links.length
        ? {
            datasets: [...new Set(links.map(link => link.dataset))],
            tables: links.map(({ dataset, dimensions, metrics }) => ({ dataset, dimensions, metrics })),
          }
        : {}),
    });
  }

  public instructionsOpened(): void {
    this.tracker.track('Burt Assistant instructions opened', this.context());
  }

  public instructionsSaved(event: AssistantChatInstructionsSaved): void {
    this.tracker.track('Burt Assistant instructions saved', { ...this.context(), ...event });
  }

  public instructionsFailed(event: AssistantChatInstructionsFailed): void {
    this.tracker.track('Burt Assistant instructions failed', { ...this.context(), ...event });
  }
}
